import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import HomeIcon from '@mui/icons-material/Home';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import BlendIcon from '@mui/icons-material/Blender';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RoomIcon from '@mui/icons-material/Room';
import ImageIcon from '@mui/icons-material/Image';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Skeleton,
  Slider,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Botel, botelService, CreateBotelRequest, Compartment, CreateCompartmentRequest } from '../../services/api/botelService';
import GooglePlacesAutocomplete from '../common/GooglePlacesAutocomplete';
import api from '../../services/api/axiosConfig';

const deviceStatusOptions = [
  { value: 'online', label: 'Online', color: 'success' },
  { value: 'offline', label: 'Offline', color: 'default' },
  { value: 'maintenance', label: 'Maintenance', color: 'warning' },
  { value: 'error', label: 'Error', color: 'error' }
];

const botelModeOptions = [
  { value: 'STR', label: 'Short-Term Rental', color: 'primary', icon: <HomeIcon /> },
  { value: 'DELIVERY', label: 'Package Delivery', color: 'secondary', icon: <LocalShippingIcon /> },
  { value: 'HYBRID', label: 'Hybrid Mode', color: 'info', icon: <BlendIcon /> }
];

const BotelManagement = () => {
  const [botels, setBotels] = useState<Botel[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [deletingBotelId, setDeletingBotelId] = useState<string | null>(null);
  const [modeChangingBotelId, setModeChangingBotelId] = useState<string | null>(null);
  const [compartmentsLoading, setCompartmentsLoading] = useState(false);
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingBotel, setEditingBotel] = useState<Botel | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [partners, setPartners] = useState<any[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  
  // New unified system state
  const [selectedBotel, setSelectedBotel] = useState<Botel | null>(null);
  const [viewCompartments, setViewCompartments] = useState(false);
  const [compartments, setCompartments] = useState<Compartment[]>([]);
  const [openCompartmentDialog, setOpenCompartmentDialog] = useState(false);
  const [compartmentFormData, setCompartmentFormData] = useState<CreateCompartmentRequest>({
    name: '',
    description: '',
    price_per_hour: 0,
    storage_capacity: 1,
    amenities: {}
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  
  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 1000]);
  const [capacityFilter, setCapacityFilter] = useState<[number, number]>([1, 10]);
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
  
  const [formData, setFormData] = useState<CreateBotelRequest>({
    host_partner_id: '',
    name: '',
    description: '',
    location: '',
    amenities: {},
    images: [],
    cleaning_fee: 0,
    service_fee: 0,
    minimum_stay: 1,
    maximum_stay: 30,
    device_serial: '',
    location_address: '',
    latitude: 0,
    longitude: 0,
    remote_lock_id: '',
    installation_date: new Date(),
    device_status: 'online',
    // New unified system fields
    mode: 'HYBRID',
    carrier_sla_enabled: false
  });
  useEffect(() => {
    fetchBotels();
  }, [currentPage, pageSize, sortBy, sortOrder]);

  const fetchBotels = async () => {
    setLoading(true);
    try {
      const filters = {
        status: statusFilter || undefined,
        minPrice: priceRange[0] > 0 ? priceRange[0] : undefined,
        maxPrice: priceRange[1] < 1000 ? priceRange[1] : undefined,
        minCapacity: capacityFilter[0] > 1 ? capacityFilter[0] : undefined,
        maxCapacity: capacityFilter[1] < 10 ? capacityFilter[1] : undefined
      };

      // Remove undefined values from filters
      Object.keys(filters).forEach(key => {
        if (filters[key as keyof typeof filters] === undefined) {
          delete filters[key as keyof typeof filters];
        }
      });

      const response = await botelService.getAllBotels(
        currentPage, 
        pageSize, 
        sortBy, 
        sortOrder, 
        searchTerm || undefined,
        Object.keys(filters).length > 0 ? filters : undefined
      );
      setBotels(response.data);
      setTotalPages(response.totalPages);
      setTotalItems(response.totalItems);
    } catch (error) {
      showSnackbar('Failed to fetch botels', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchBotels();
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const handlePageSizeChange = (event: any) => {
    setPageSize(parseInt(event.target.value));
    setCurrentPage(1);
  };

  const handleSortChange = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortOrder('ASC');
    }
  };

  const applyFilters = () => {
    setCurrentPage(1);
    fetchBotels();
  };

  const clearFilters = () => {
    setStatusFilter('');
    setPriceRange([0, 1000]);
    setCapacityFilter([1, 10]);
    setSearchTerm('');
    setCurrentPage(1);
    fetchBotels();
  };

  // Remove the client-side filtering since we're now doing server-side filtering
  const displayedBotels = botels;
  
  const handleCreate = () => {
    setEditingBotel(null);
    setFormData({
      host_partner_id: '',
      name: '',
      description: '',
      location: '',
      amenities: {},
      images: [],
      cleaning_fee: 0,
      service_fee: 0,
      minimum_stay: 1,
      maximum_stay: 30,
      device_serial: '',
      location_address: '',
      latitude: 0,
      longitude: 0,
      remote_lock_id: '',
      installation_date: new Date(),
      device_status: 'online',
      mode: 'HYBRID',
      carrier_sla_enabled: false
    });
    setImagePreviews([]);
    setOpenDialog(true);
  };

  const handleEdit = (botel: Botel) => {
    setEditingBotel(botel);
    setFormData({
      host_partner_id: botel.host_partner_id,
      name: botel.name,
      description: botel.description,
      location: botel.location,

      amenities: botel.amenities || {},
      images: botel.images || [],
      cleaning_fee: botel.cleaning_fee || 0,
      service_fee: botel.service_fee || 0,
      minimum_stay: botel.minimum_stay || 1,
      maximum_stay: botel.maximum_stay || 30,
      device_serial: botel.device_serial || '',
      location_address: botel.location_address || '',
      latitude: botel.latitude || 0,
      longitude: botel.longitude || 0,
      remote_lock_id: botel.remote_lock_id || '',
      installation_date: botel.installation_date || new Date(),
      device_status: botel.device_status || 'online',
      mode: botel.mode || 'HYBRID',
      carrier_sla_enabled: botel.carrier_sla_enabled || false
    });
    setImagePreviews(botel.images || []);
    setOpenDialog(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this botel?')) {
      try {
        setDeletingBotelId(id);
        await botelService.deleteBotel(id);
        showSnackbar('Botel deleted successfully', 'success');
        fetchBotels();
      } catch (error) {
        showSnackbar('Failed to delete botel', 'error');
      } finally {
        setDeletingBotelId(null);
      }
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setImagePreviews([]);
  };

  const handleSubmit = async () => {
    try {
      // Validate that a partner is selected when creating a new botel
      if (!editingBotel && !formData.host_partner_id) {
        showSnackbar('Please select a host partner', 'error');
        return;
      }

      setSubmitLoading(true);
      if (editingBotel) {
        await botelService.updateBotel(editingBotel.id, formData);
        showSnackbar('Botel updated successfully', 'success');
      } else {
        // Ensure host_partner_id is set when creating
        const botelData = {
          ...formData,
          host_partner_id: formData.host_partner_id
        };
        await botelService.createBotel(botelData);
        showSnackbar('Botel created successfully', 'success');
      }
      handleCloseDialog();
      fetchBotels();
    } catch (error) {
      showSnackbar('Failed to save botel', 'error');
    } finally {
      setSubmitLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const statusColor = (status: string) => {
    const statusOption = deviceStatusOptions.find(option => option.value === status);
    return statusOption?.color || 'default';
  };

  const modeColor = (mode: string) => {
    const modeOption = botelModeOptions.find(option => option.value === mode);
    return modeOption?.color || 'default';
  };

  const modeIcon = (mode: string) => {
    const modeOption = botelModeOptions.find(option => option.value === mode);
    return modeOption?.icon || <HomeIcon />;
  };

  // Handle Google Places selection
  const handlePlaceSelect = (place: { address: string; latitude: number; longitude: number; formatted_address: string }) => {
    setFormData(prev => ({
      ...prev,
      location: place.address,
      location_address: place.formatted_address,
      latitude: place.latitude,
      longitude: place.longitude
    }));
  };

  // Handle image upload
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'));
    if (imageFiles.length === 0) return;

    const promises = imageFiles.map((file) => {
      return new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve(reader.result as string);
        };
        reader.readAsDataURL(file);
      });
    });

    Promise.all(promises).then((base64Images) => {
      setFormData(prev => ({
        ...prev,
        images: [...(prev.images || []), ...base64Images]
      }));
      setImagePreviews(prev => [...prev, ...base64Images]);
    });
  };

  // Handle image removal
  const handleRemoveImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: (prev.images || []).filter((_, i) => i !== index)
    }));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  // New unified system functions
  const handleModeChange = async (botelId: string, newMode: 'STR' | 'DELIVERY' | 'HYBRID') => {
    try {
      setModeChangingBotelId(botelId);
      await botelService.setBotelMode(botelId, newMode);
      showSnackbar(`Botel mode changed to ${newMode}`, 'success');
      fetchBotels();
    } catch (error) {
      showSnackbar('Failed to change botel mode', 'error');
    } finally {
      setModeChangingBotelId(null);
    }
  };

  const handleViewCompartments = async (botel: Botel) => {
    try {
      setSelectedBotel(botel);
      setCompartmentsLoading(true);
      setViewCompartments(true);
      const compartmentData = await botelService.getCompartments(botel.id);
      setCompartments(compartmentData);
    } catch (error) {
      showSnackbar('Failed to load compartments', 'error');
    } finally {
      setCompartmentsLoading(false);
    }
  };

  const handleCreateCompartment = async () => {
    if (!selectedBotel) return;
    
    try {
      setSubmitLoading(true);
      await botelService.createCompartment(selectedBotel.id, compartmentFormData);
      showSnackbar('Compartment created successfully', 'success');
      setOpenCompartmentDialog(false);
      
      // Reset form
      setCompartmentFormData({
        name: '',
        description: '',
        price_per_hour: 0,
        storage_capacity: 1,
        amenities: {}
      });
      
      // Refresh compartments
      setCompartmentsLoading(true);
      const compartmentData = await botelService.getCompartments(selectedBotel.id);
      setCompartments(compartmentData);
    } catch (error) {
      showSnackbar('Failed to create compartment', 'error');
    } finally {
      setSubmitLoading(false);
      setCompartmentsLoading(false);
    }
  };
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        setPartnersLoading(true);
        const response = await api.get<{ success: boolean; data: any[]; total: number }>('/partners');
        if (response.data.success) {
          setPartners(response.data.data);
        }
      } catch (error) {
        console.error('Failed to fetch partners:', error);
      } finally {
        setPartnersLoading(false);
      }
    };

    fetchPartners();
  }, []);

  return (
    <Card sx={{ p: 3, background: '#181F2A', boxShadow: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
          Botel Management
        </Typography>
        <Button 
          variant="contained" 
          sx={{ background: '#3456D5', minWidth: 100 }} 
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          Add Botel
        </Button>
      </Box>
      
      {/* Search and Filters */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', mb: 2, gap: 2 }}>
          <TextField
            variant="outlined"
            placeholder="Search by Botel Name or Location"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ background: '#232B3E', borderRadius: 1, input: { color: '#fff' }, flex: 1 }}
            InputProps={{ style: { color: '#fff' } }}
          />
          <Button 
            variant="contained" 
            sx={{ background: '#3456D5', minWidth: 100 }} 
            startIcon={<SearchIcon />}
            onClick={handleSearch}
          >
            Search
          </Button>
          <Button 
            variant="outlined" 
            sx={{ borderColor: '#3456D5', color: '#3456D5', minWidth: 100 }} 
            startIcon={<FilterListIcon />}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filters
          </Button>
        </Box>

        {/* Advanced Filters */}
        <Collapse in={showFilters}>
          <Paper sx={{ p: 2, background: '#232B3E', mb: 2 }}>
            <Grid container spacing={3}>
              <Grid size={{xs: 12, md: 3}}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ color: '#fff' }}>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    sx={{ color: '#fff', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#3456D5' } }}
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    {deviceStatusOptions.map((status) => (
                      <MenuItem key={status.value} value={status.value}>
                        {status.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid size={{xs: 12, md: 3}}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ color: '#fff' }}>Sort By</InputLabel>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    sx={{ color: '#fff', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#3456D5' } }}
                  >
                    <MenuItem value="name">Name</MenuItem>
                    <MenuItem value="price_per_night">Price</MenuItem>
                    <MenuItem value="capacity">Capacity</MenuItem>
                    <MenuItem value="created_at">Created Date</MenuItem>
                    <MenuItem value="device_status">Status</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{xs: 12, md: 3}}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ color: '#fff' }}>Sort Order</InputLabel>
                  <Select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value as 'ASC' | 'DESC')}
                    sx={{ color: '#fff', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#3456D5' } }}
                  >
                    <MenuItem value="ASC">Ascending</MenuItem>
                    <MenuItem value="DESC">Descending</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{xs: 12, md: 3}}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ color: '#fff' }}>Page Size</InputLabel>
                  <Select
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    sx={{ color: '#fff', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#3456D5' } }}
                  >
                    <MenuItem value={5}>5 per page</MenuItem>
                    <MenuItem value={10}>10 per page</MenuItem>
                    <MenuItem value={25}>25 per page</MenuItem>
                    <MenuItem value={50}>50 per page</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{xs: 12, md: 6}}>
                <Typography variant="body2" sx={{ color: '#fff', mb: 1 }}>
                  Price Range: ${priceRange[0]} - ${priceRange[1]}
                </Typography>
                <Slider
                  value={priceRange}
                  onChange={(event, newValue) => setPriceRange(newValue as [number, number])}
                  valueLabelDisplay="auto"
                  min={0}
                  max={1000}
                  sx={{ color: '#3456D5' }}
                />
              </Grid>

              <Grid size={{xs: 12, md: 6}}>
                <Typography variant="body2" sx={{ color: '#fff', mb: 1 }}>
                  Capacity Range: {capacityFilter[0]} - {capacityFilter[1]} guests
                </Typography>
                <Slider
                  value={capacityFilter}
                  onChange={(event, newValue) => setCapacityFilter(newValue as [number, number])}
                  valueLabelDisplay="auto"
                  min={1}
                  max={20}
                  sx={{ color: '#3456D5' }}
                />
              </Grid>

              <Grid size={{xs: 12}}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button 
                    variant="contained" 
                    sx={{ background: '#3456D5' }} 
                    onClick={applyFilters}
                  >
                    Apply Filters
                  </Button>
                  <Button 
                    variant="outlined" 
                    sx={{ borderColor: '#3456D5', color: '#3456D5' }} 
                    onClick={clearFilters}
                  >
                    Clear Filters
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Collapse>
      </Box>

      {/* Results Summary */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" sx={{ color: '#fff' }}>
          Showing {displayedBotels.length} of {totalItems} botels
        </Typography>
        <Typography variant="body2" sx={{ color: '#fff' }}>
          Page {currentPage} of {totalPages}
        </Typography>
      </Box>

      <TableContainer component={Paper} sx={{ background: 'transparent' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ background: '#232B3E' }}>
              <TableCell 
                sx={{ color: '#fff', fontWeight: 700, cursor: 'pointer' }}
                onClick={() => handleSortChange('name')}
              >
                Botel Name {sortBy === 'name' && (sortOrder === 'ASC' ? '↑' : '↓')}
              </TableCell>
              <TableCell 
                sx={{ color: '#fff', fontWeight: 700, cursor: 'pointer' }}
                onClick={() => handleSortChange('location')}
              >
                Location {sortBy === 'location' && (sortOrder === 'ASC' ? '↑' : '↓')}
              </TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 700 }}>
                Host Partner
              </TableCell>
              <TableCell 
                sx={{ color: '#fff', fontWeight: 700, cursor: 'pointer' }}
                onClick={() => handleSortChange('device_status')}
              >
                Status {sortBy === 'device_status' && (sortOrder === 'ASC' ? '↑' : '↓')}
              </TableCell>
              <TableCell 
                sx={{ color: '#fff', fontWeight: 700, cursor: 'pointer' }}
                onClick={() => handleSortChange('mode')}
              >
                Mode {sortBy === 'mode' && (sortOrder === 'ASC' ? '↑' : '↓')}
              </TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              [...Array(5)].map((_, idx) => (
                <TableRow key={idx} sx={{ background: idx % 2 ? '#1C2533' : 'transparent' }}>
                  <TableCell colSpan={6} sx={{ py: 3 }}>
                    <Skeleton variant="rectangular" height={20} sx={{ bgcolor: '#232B3E', borderRadius: 1 }} />
                  </TableCell>
                </TableRow>
              ))
            ) : displayedBotels.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6, border: 'none' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <HomeIcon sx={{ fontSize: 64, color: '#666' }} />
                    <Typography variant="h6" sx={{ color: '#b0b8c1' }}>
                      No botels found
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      {searchTerm || statusFilter ? 'Try adjusting your filters' : 'Create your first botel to get started'}
                    </Typography>
                    {!searchTerm && !statusFilter && (
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleCreate}
                        sx={{ background: '#3456D5', mt: 1 }}
                      >
                        Create Botel
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              displayedBotels.map((botel, idx) => (
                <TableRow key={botel.id} sx={{ background: idx % 2 ? '#1C2533' : 'transparent' }}>
                  <TableCell sx={{ color: '#fff' }}>{botel.name}</TableCell>
                  <TableCell sx={{ color: '#fff' }}>{botel.location_address}</TableCell>
                  <TableCell sx={{ color: '#fff' }}>
                    {botel.host_partner?.company_name || 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={botel.device_status}
                      color={statusColor(botel.device_status) as any}
                      size="small"
                      sx={{ fontWeight: 700 }}
                    />
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" sx={{ minWidth: 120, position: 'relative' }}>
                      <Select
                        value={botel.mode || 'HYBRID'}
                        onChange={(e) => handleModeChange(botel.id, e.target.value as any)}
                        disabled={modeChangingBotelId === botel.id}
                        sx={{ 
                          color: '#fff', 
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: modeColor(botel.mode || 'HYBRID') } 
                        }}
                      >
                        {botelModeOptions.map((mode) => (
                          <MenuItem key={mode.value} value={mode.value}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {mode.icon}
                              {mode.value}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                      {modeChangingBotelId === botel.id && (
                        <CircularProgress size={16} sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }} />
                      )}
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton 
                        color="primary" 
                        onClick={() => handleEdit(botel)}
                        disabled={deletingBotelId === botel.id}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        color="info" 
                        onClick={() => handleViewCompartments(botel)}
                        disabled={deletingBotelId === botel.id}
                      >
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton 
                        color="error" 
                        onClick={() => handleDelete(botel.id)}
                        disabled={deletingBotelId === botel.id}
                      >
                        {deletingBotelId === botel.id ? (
                          <CircularProgress size={20} color="error" />
                        ) : (
                          <DeleteIcon />
                        )}
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            sx={{
              '& .MuiPaginationItem-root': {
                color: '#fff',
                '&.Mui-selected': {
                  backgroundColor: '#3456D5',
                },
              },
            }}
          />
        </Box>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ background: '#181F2A', color: '#fff' }}>
          {editingBotel ? 'Edit Botel' : 'Create New Botel'}
        </DialogTitle>
        <DialogContent sx={{ background: '#181F2A', color: '#fff' }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{xs: 12, md: 6}}>
              <TextField
                fullWidth
                label="Botel Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                sx={{ input: { color: '#fff' }, label: { color: '#fff' } }}
              />
            </Grid>
            <Grid size={{xs: 12, md: 6}}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#fff' }}>Host Partners</InputLabel>
                <Select
                  value={formData.host_partner_id || ''}
                  onChange={(e) => {
                    const selectedPartnerId = e.target.value as string;
                    setFormData({ ...formData, host_partner_id: selectedPartnerId });
                  }}
                  sx={{ color: '#fff', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#3456D5' } }}
                >
                  <MenuItem value="">
                    <em>Select a partner</em>
                  </MenuItem>
                  {partners.map((partner) => (
                    <MenuItem key={partner.id} value={partner.id}>
                      {partner.company_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{xs: 12}}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                sx={{ input: { color: '#fff' }, label: { color: '#fff' } }}
              />
            </Grid>
            <Grid size={{xs: 12, md: 6}}>
              <TextField
                fullWidth
                label="Location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                sx={{ input: { color: '#fff' }, label: { color: '#fff' } }}
                placeholder="Enter location (e.g., 'New York, NY')"
              />
            </Grid>
            <Grid size={{xs: 12, md: 6}}>
              <GooglePlacesAutocomplete
                label="Address"
                placeholder="Search for address (e.g., '123 Main St, New York, NY')"
                value={formData.location_address}
                onPlaceSelect={handlePlaceSelect}
                onChange={(value) => setFormData({ ...formData, location_address: value })}
                sx={{ mb: 1 }}
              />
            </Grid>

            <Grid size={{xs: 12, md: 4}}>
              <TextField
                fullWidth
                label="Device Serial"
                value={formData.device_serial}
                onChange={(e) => setFormData({ ...formData, device_serial: e.target.value })}
                sx={{ input: { color: '#fff' }, label: { color: '#fff' } }}
              />
            </Grid>
            <Grid size={{xs: 12, md: 6}}>
              <TextField
                fullWidth
                type="number"
                label="Cleaning Fee"
                value={formData.cleaning_fee}
                onChange={(e) => setFormData({ ...formData, cleaning_fee: parseFloat(e.target.value) })}
                sx={{ input: { color: '#fff' }, label: { color: '#fff' } }}
              />
            </Grid>
            <Grid size={{xs: 12, md: 6}}>
              <TextField
                fullWidth
                type="number"
                label="Service Fee"
                value={formData.service_fee}
                onChange={(e) => setFormData({ ...formData, service_fee: parseFloat(e.target.value) })}
                sx={{ input: { color: '#fff' }, label: { color: '#fff' } }}
              />
            </Grid>
            <Grid size={{xs: 12, md: 6}}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#fff' }}>Operating Mode</InputLabel>
                <Select
                  value={formData.mode || 'HYBRID'}
                  onChange={(e) => setFormData({ ...formData, mode: e.target.value as any })}
                  sx={{ color: '#fff', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#3456D5' } }}
                >
                  {botelModeOptions.map((mode) => (
                    <MenuItem key={mode.value} value={mode.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {mode.icon}
                        {mode.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{xs: 12, md: 6}}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#fff' }}>Device Status</InputLabel>
                <Select
                  value={formData.device_status || 'online'}
                  onChange={(e) => setFormData({ ...formData, device_status: e.target.value })}
                  sx={{ color: '#fff', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#3456D5' } }}
                >
                  {deviceStatusOptions.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip 
                          label={status.label} 
                          size="small" 
                          color={status.color as any}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{xs: 12, md: 6}}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#fff' }}>Carrier SLA</InputLabel>
                <Select
                  value={formData.carrier_sla_enabled ? 'enabled' : 'disabled'}
                  onChange={(e) => setFormData({ ...formData, carrier_sla_enabled: e.target.value === 'enabled' })}
                  sx={{ color: '#fff', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#3456D5' } }}
                >
                  <MenuItem value="disabled">Disabled</MenuItem>
                  <MenuItem value="enabled">Enabled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{xs: 12}}>
              <Box>
                <Typography variant="body2" sx={{ color: '#fff', mb: 1 }}>
                  Botel Images
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<ImageIcon />}
                  sx={{
                    borderColor: '#3456D5',
                    color: '#3456D5',
                    mb: 2,
                    '&:hover': {
                      borderColor: '#4A6FE8',
                      backgroundColor: 'rgba(52, 86, 213, 0.1)'
                    }
                  }}
                >
                  Upload Images
                  <input
                    type="file"
                    hidden
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                </Button>
                {imagePreviews.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                    {imagePreviews.map((preview, index) => (
                      <Box
                        key={index}
                        sx={{
                          position: 'relative',
                          width: 150,
                          height: 150,
                          borderRadius: 2,
                          overflow: 'hidden',
                          border: '1px solid #3456D5'
                        }}
                      >
                        <img
                          src={preview}
                          alt={`Preview ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveImage(index)}
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            color: '#fff',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.8)'
                            }
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ background: '#181F2A' }}>
          <Button 
            onClick={handleCloseDialog} 
            sx={{ color: '#fff' }}
            disabled={submitLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            sx={{ background: '#3456D5' }}
            disabled={submitLoading}
            startIcon={submitLoading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {submitLoading ? (editingBotel ? 'Updating...' : 'Creating...') : (editingBotel ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Compartment Management Dialog */}
      <Dialog 
        open={viewCompartments} 
        onClose={() => setViewCompartments(false)} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle sx={{ background: '#181F2A', color: '#fff' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Compartments - {selectedBotel?.name}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenCompartmentDialog(true)}
              sx={{ background: '#3456D5' }}
              disabled={compartmentsLoading}
            >
              Add Compartment
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ background: '#181F2A', color: '#fff' }}>
          {compartmentsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress sx={{ color: '#3456D5' }} />
            </Box>
          ) : compartments.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <RoomIcon sx={{ fontSize: 64, color: '#666', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#b0b8c1', mb: 1 }}>
                No compartments found
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
                Create your first compartment to start managing bookings
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenCompartmentDialog(true)}
                sx={{ background: '#3456D5' }}
              >
                Create Compartment
              </Button>
            </Box>
          ) : (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {compartments.map((compartment) => (
                <Grid size={{xs: 12, md: 6, lg: 4}} key={compartment.id}>
                  <Card sx={{ background: '#232B3E', color: '#fff' }}>
                    <Box sx={{ p: 2 }}>
                      <Typography variant="h6" sx={{ mb: 1 }}>
                        {compartment.name}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2, color: '#ccc' }}>
                        {compartment.description || 'No description'}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Type:</Typography>
                        <Chip 
                          label={compartment.comp_type} 
                          size="small" 
                          color="primary" 
                        />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Occupancy:</Typography>
                        <Chip 
                          label={compartment.occupancy_mode} 
                          size="small" 
                          color="secondary" 
                        />
                      </Box>
                      {compartment.price_per_hour && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">Price/Hour:</Typography>
                          <Typography variant="body2">${compartment.price_per_hour}</Typography>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Storage:</Typography>
                        <Typography variant="body2">{compartment.storage_capacity} items</Typography>
                      </Box>
                      {compartment.capacity && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">Max Units:</Typography>
                          <Typography variant="body2">{compartment.capacity.max_units}</Typography>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Status:</Typography>
                        <Chip 
                          label={compartment.is_active ? 'Active' : 'Inactive'} 
                          size="small" 
                          color={compartment.is_active ? 'success' : 'default'} 
                        />
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ background: '#181F2A' }}>
          <Button onClick={() => setViewCompartments(false)} sx={{ color: '#fff' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Compartment Dialog */}
      <Dialog 
        open={openCompartmentDialog} 
        onClose={() => setOpenCompartmentDialog(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ background: '#181F2A', color: '#fff' }}>
          Create New Compartment
        </DialogTitle>
        <DialogContent sx={{ background: '#181F2A', color: '#fff' }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{xs: 12}}>
              <TextField
                fullWidth
                label="Compartment Name"
                value={compartmentFormData.name}
                onChange={(e) => setCompartmentFormData({ ...compartmentFormData, name: e.target.value })}
                sx={{ input: { color: '#fff' }, label: { color: '#fff' } }}
              />
            </Grid>
            <Grid size={{xs: 12}}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={compartmentFormData.description}
                onChange={(e) => setCompartmentFormData({ ...compartmentFormData, description: e.target.value })}
                sx={{ input: { color: '#fff' }, label: { color: '#fff' } }}
              />
            </Grid>
            <Grid size={{xs: 12, md: 6}}>
              <TextField
                fullWidth
                type="number"
                label="Price per Hour (optional)"
                value={compartmentFormData.price_per_hour || ''}
                onChange={(e) => setCompartmentFormData({ ...compartmentFormData, price_per_hour: parseFloat(e.target.value) || 0 })}
                sx={{ input: { color: '#fff' }, label: { color: '#fff' } }}
              />
            </Grid>
            <Grid size={{xs: 12, md: 6}}>
              <TextField
                fullWidth
                type="number"
                label="Storage Capacity"
                value={compartmentFormData.storage_capacity || 1}
                onChange={(e) => setCompartmentFormData({ ...compartmentFormData, storage_capacity: parseInt(e.target.value) || 1 })}
                sx={{ input: { color: '#fff' }, label: { color: '#fff' } }}
                helperText="Number of items that can be stored"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ background: '#181F2A' }}>
          <Button 
            onClick={() => setOpenCompartmentDialog(false)} 
            sx={{ color: '#fff' }}
            disabled={submitLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateCompartment} 
            variant="contained" 
            sx={{ background: '#3456D5' }}
            disabled={submitLoading}
            startIcon={submitLoading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {submitLoading ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Backdrop for loading states */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading && displayedBotels.length === 0}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default BotelManagement; 